// KeyboardShortcutsModal.tsx - Modal de ajuda de atalhos
// v4.0 - Janeiro 2026 - Alt+1/2/3 para janelas flutuantes
import React from 'react';
import './KeyboardShortcutsModal.css';
import { CloseIcon } from './bigwa-icons';

interface KeyboardShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function KeyboardShortcutsModal({ isOpen, onClose }: KeyboardShortcutsModalProps) {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const modKey = isMac ? '⌘' : 'Ctrl';
  const altKey = isMac ? '⌥' : 'Alt';
  const deleteKey = isMac ? '⌫' : 'Del';

  if (!isOpen) return null;

  const shortcutGroups = [
    {
      title: 'Global',
      shortcuts: [
        { keys: [modKey, ','], description: 'Preferências' },
        { keys: [modKey, '/'], description: 'Mostrar atalhos' },
        { keys: [modKey, '⇧', 'K'], description: 'Busca global' },
        { keys: [modKey, 'K'], description: 'Bigwa Search (em Leituras)' },
        { keys: [modKey, 'S'], description: 'Sincronizar' },
        { keys: [modKey, '\\'], description: 'Recolher/expandir barra lateral' },
        { keys: ['Esc'], description: 'Fechar / Cancelar' },
      ]
    },
    {
      title: 'Janelas',
      description: 'Flutuantes',
      shortcuts: [
        { keys: [altKey, '1'], description: 'Tarefas' },
        { keys: [altKey, '2'], description: 'Outliner' },
        { keys: [altKey, '3'], description: 'Bloco de Ideias' },
      ]
    },
    {
      title: 'Navegação',
      description: 'G + letra',
      shortcuts: [
        { keys: ['G', 'H'], description: 'Início' },
        { keys: ['G', 'B'], description: 'Biblioteca' },
        { keys: ['G', 'L'], description: 'Fichamentos' },
        { keys: ['G', 'D'], description: 'Dados' },
        { keys: ['G', 'A'], description: 'Arquivo' },
      ]
    },
    {
      title: 'Lista',
      description: 'Biblioteca / Leituras',
      shortcuts: [
        { keys: ['J'], description: 'Próximo item' },
        { keys: ['K'], description: 'Item anterior' },
        { keys: ['Enter'], description: 'Abrir selecionado' },
        { keys: [modKey, deleteKey], description: 'Mover para lixeira' },
      ]
    },
    {
      title: 'Inspector',
      shortcuts: [
        { keys: [modKey, '⇧', 'I'], description: 'Expandir/recolher (global)' },
        { keys: ['I'], description: 'Mostrar/ocultar (em listas)' },
        { keys: ['1', '~', '5'], description: 'Trocar aba' },
      ]
    },
    {
      title: 'Ações',
      shortcuts: [
        { keys: [modKey, 'N'], description: 'Nova referência' },
        { keys: [modKey, 'T'], description: 'Nova tarefa' },
        { keys: [modKey, '⇧', 'F'], description: 'Favoritar' },
        { keys: [modKey, '⇧', deleteKey], description: 'Esvaziar lixeira' },
      ]
    },
  ];

  return (
    <div className="shortcuts-modal-overlay" onClick={onClose}>
      <div className="shortcuts-modal" onClick={e => e.stopPropagation()}>
        <div className="shortcuts-modal-header">
          <h2>Atalhos de Teclado</h2>
          <button className="shortcuts-modal-close" onClick={onClose}><CloseIcon size={20} /></button>
        </div>
        <div className="shortcuts-modal-content">
          {shortcutGroups.map(group => (
            <div key={group.title} className="shortcuts-group">
              <div className="shortcuts-group-header">
                <h3>{group.title}</h3>
                {group.description && <span className="shortcuts-group-description">{group.description}</span>}
              </div>
              <div className="shortcuts-list">
                {group.shortcuts.map((shortcut, idx) => (
                  <div key={idx} className="shortcut-item">
                    <span className="shortcut-description">{shortcut.description}</span>
                    <span className="shortcut-keys">
                      {shortcut.keys.map((key, keyIdx) => (
                        <React.Fragment key={keyIdx}>
                          {key === '~' ? <span className="key-range">a</span> : <kbd>{key}</kbd>}
                          {keyIdx < shortcut.keys.length - 1 && key !== '~' && shortcut.keys[keyIdx + 1] !== '~' && (
                            <span className="key-separator">+</span>
                          )}
                        </React.Fragment>
                      ))}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="shortcuts-modal-footer">
          <span className="shortcuts-tip">Pressione <kbd>G</kbd> + letra para navegar</span>
        </div>
      </div>
    </div>
  );
}
